import { Map } from "@pdfking/tfunc"

import type { SearchCacheBackend } from "../types/CacheBackend"

const store = "Default" as const

const to_promise = (request: IDBRequest): Promise<any> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

export function MetaCache<V>(database: string): SearchCacheBackend<V> {
  const open = (
    mode: "readonly" | "readwrite" = "readwrite"
  ): Promise<IDBObjectStore> => {
    const request = indexedDB.open(database, 1)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(store))
        db.createObjectStore(store, { keyPath: "id" })
    }
    return to_promise(request).then((result: IDBDatabase) =>
      result.transaction(store, mode).objectStore(store)
    )
  }

  return {
    startswith: (prefix?: string): Promise<Record<string, V>> =>
      open("readonly").then(db =>
        to_promise(
          db.getAll(
            prefix ? IDBKeyRange.bound(prefix, prefix + "\uffff") : undefined
          )
        ).then((results: { id: string; data: V }[]) =>
          Object.fromEntries(Map(({ id, data }) => [id, data], results))
        )
      ),

    exists: (id: string): Promise<boolean> =>
      open("readonly")
        .then(db => to_promise(db.get(id)))
        .then(result => (result ? true : false)),

    get: (id: string): Promise<undefined | V> =>
      open("readonly")
        .then(db => to_promise(db.get(id)))
        .then(result => (result ? result.data : undefined)),

    set: (id: string, data: V): Promise<void> =>
      open().then(db =>
        to_promise(
          db.put({
            id,
            data,
          })
        )
      ),

    delete: (id: string): Promise<void> =>
      open().then(db => to_promise(db.delete(id))),

    clear: (): Promise<void> => open().then(db => to_promise(db.clear())),
  }
}
